<template>
  <header class="text-black py-4 sticky top-0 z-50 bg-white">
    <div class="mx-auto flex justify-between items-center px-4">
      <NuxtLink to="/">
        <div class="flex items-center space-x-2">
          <!-- Peachy Pro Studios Logo -->
          <svg class="w-10 h-10" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <!-- Peach shape -->
            <circle cx="50" cy="52" r="35" fill="url(#peachGradient)" />
            
            <!-- Peach highlight -->
            <ellipse cx="38" cy="40" rx="12" ry="15" fill="url(#highlightGradient)" opacity="0.4" />
            
            <!-- Leaf -->
            <path d="M50 15 C 45 15, 42 18, 42 23 C 42 28, 45 32, 50 32 C 50 32, 50 15, 50 15 Z" 
                  fill="url(#leafGradient)" />
            <path d="M50 15 C 55 15, 58 18, 58 23 C 58 28, 55 32, 50 32 C 50 32, 50 15, 50 15 Z" 
                  fill="url(#leafGradient2)" opacity="0.8" />
            
            <!-- Peach crease -->
            <path d="M50 17 Q 52 35, 50 87" stroke="url(#creaseGradient)" stroke-width="2.5" 
                  stroke-linecap="round" fill="none" opacity="0.3" />
            
            <!-- Play button overlay -->
            <circle cx="50" cy="52" r="15" fill="white" opacity="0.95" />
            <path d="M46 44 L46 60 L62 52 Z" fill="url(#playGradient)" />
            
            <!-- Definitions -->
            <defs>
              <linearGradient id="peachGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#FF6B9D;stop-opacity:1" />
                <stop offset="50%" style="stop-color:#FFA07A;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#FF8C69;stop-opacity:1" />
              </linearGradient>
              <linearGradient id="highlightGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#FFE5E5;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#FFB6C1;stop-opacity:0.5" />
              </linearGradient>
              <linearGradient id="leafGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#7CFC00;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#32CD32;stop-opacity:1" />
              </linearGradient>
              <linearGradient id="leafGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#98FB98;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#3CB371;stop-opacity:1" />
              </linearGradient>
              <linearGradient id="creaseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" style="stop-color:#8B4513;stop-opacity:0.4" />
                <stop offset="100%" style="stop-color:#D2691E;stop-opacity:0.6" />
              </linearGradient>
              <linearGradient id="playGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#EF4444;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#EC4899;stop-opacity:1" />
              </linearGradient>
            </defs>
          </svg>
          <div class="flex flex-col">
            <span class="text-xl font-bold text-gray-900 leading-tight">Peachy Pro</span>
            <span class="text-xs text-gray-500 leading-tight">Studios</span>
          </div>
        </div>
        <div v-if="userRole" class="text-xs">
          {{ userRole }}
        </div>
      </NuxtLink>
      <nav class="relative">
        <!-- Desktop Navigation -->
        <div class="hidden md:flex space-x-4">
          <SignedOut>
            <div class="flex gap-2 items-center space-x-8">
              <a href="#features" class="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
              <a href="#pricing" class="text-gray-600 hover:text-gray-900 transition-colors">Pricing</a>
              <a href="#process" class="text-gray-600 hover:text-gray-900 transition-colors">How it Works</a>

              <NuxtLink to="/auth/login" class="text-gray-600 hover:text-gray-900 transition-colors">
                Sign In
              </NuxtLink>
              <NuxtLink to="/request-demo"
                class="bg-gradient-to-r from-red-500 to-pink-600 text-white px-6 py-2.5 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-200 font-medium">
                Request a demo
              </NuxtLink>
            </div>
          </SignedOut>
          <SignedIn>
            <div class="flex items-center gap-4">
              <div>{{ loggedInUser?.email }}</div>

              <!-- Timezone Selector -->
              <div class="relative">
                <button @click="openTimezoneModal"
                  class="flex items-center space-x-2 px-3 py-2 rounded-md bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                  title="Change Timezone">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span class="text-sm text-gray-700">{{ displayTimezone }}</span>
                  <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              </div>

              <UserButton />
              <div v-if="route?.path.includes('/app') === false">
                <NuxtLink to="/app"
                  class="bg-gradient-to-r from-red-500 to-pink-600 text-white px-6 py-2.5 rounded-lg hover:shadow-lg hover:scale-105 transition-all duration-200 font-medium">
                  Go to App
                </NuxtLink>
              </div>
            </div>
          </SignedIn>
        </div>

        <!-- Mobile Navigation -->
        <div class="md:hidden relative z-50">
          <div class="flex flex-row">
            <div class="px-4 py-2">
              <UserButton />
            </div>
            <button @click="handleMenuClick" class="p-2">
              <Icon v-if="!isMenuOpen" name="lucide:menu" class="h-6 w-6" />
              <Icon v-else name="lucide:x" class="h-6 w-6" />
            </button>
          </div>

          <!-- Mobile Menu -->
          <div v-if="isMenuOpen"
            class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg shadow-black py-1 z-[100]">
            <div class="flex flex-col">
              <SignedOut>
                <NuxtLink to="/auth/login" @click="handleMenuClick" class="block px-4 py-2  text-black hover:bg-orange-50">
                  Sign in
                </NuxtLink>
                <NuxtLink to="/request-demo" @click="handleMenuClick" class="block px-4 py-2  text-black hover:bg-orange-50">
                  Request a demo
                </NuxtLink>
              </SignedOut>
              <SignedIn>
                <div v-if="route?.path.includes('/app') === false">
                  <NuxtLink to="/app" class="block px-4 py-2  text-black hover:bg-orange-50">
                    Go to App
                  </NuxtLink>
                </div>
                <NuxtLink to="/app" @click="handleMenuClick">
                  <div class="block px-4 py-2  text-black hover:bg-orange-50">
                    Home
                  </div>
                </NuxtLink>
                <NuxtLink v-if="isSuperAdmin" to="/app/admin" @click="handleMenuClick">
                  <div class="block px-4 py-2  text-black hover:bg-orange-50">
                    Admin
                  </div>
                </NuxtLink>
                <NuxtLink v-if="isSuperAdmin" to="/app/action-items" @click="handleMenuClick">
                  <div class="block px-4 py-2  text-black hover:bg-orange-50">
                    Action Items
                  </div>
                </NuxtLink>
                <NuxtLink v-if="isSuperAdmin" to="/app/queue-operations" @click="handleMenuClick">
                  <div class="block px-4 py-2  text-black hover:bg-orange-50">
                    Queue Operations
                  </div>
                </NuxtLink>
                <NuxtLink to="/app/projects" @click="handleMenuClick">
                  <div class="block px-4 py-2  text-black hover:bg-orange-50">
                    Projects
                  </div>
                </NuxtLink>
                <NuxtLink to="/app/queue" @click="handleMenuClick">
                  <div class="block px-4 py-2  text-black hover:bg-orange-50">
                    Queue
                  </div>
                </NuxtLink>
                <NuxtLink to="/app/calendar" @click="handleMenuClick">
                  <div class="block px-4 py-2  text-black hover:bg-orange-50">
                    Calendar
                  </div>
                </NuxtLink>
                <NuxtLink to="/app/fix-it" @click="handleMenuClick">
                  <div class="block px-4 py-2  text-black hover:bg-orange-50">
                    Fix It
                  </div>
                </NuxtLink>
                <NuxtLink to="/app/history" @click="handleMenuClick">
                  <div class="block px-4 py-2  text-black hover:bg-orange-50">
                    History
                  </div>
                </NuxtLink>
              </SignedIn>
            </div>
          </div>
        </div>
      </nav>

    </div>

    <!-- Timezone Settings Modal -->
    <div v-if="isTimezoneModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click="closeTimezoneModal">
      <div class="bg-white rounded-lg w-full max-w-md mx-4 max-h-[80vh] flex flex-col" @click.stop>
        <!-- Header -->
        <div class="flex justify-between items-center p-6 pb-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900">Timezone Settings</h3>
          <button @click="closeTimezoneModal" class="text-gray-400 hover:text-gray-600 text-xl">
            ×
          </button>
        </div>

        <!-- Current timezone info -->
        <div class="px-6 py-4 bg-gray-50">
          <p class="text-sm text-gray-600">Current timezone: {{ currentTimezone }}</p>
        </div>

        <!-- Scrollable timezone list -->
        <div class="flex-1 overflow-y-auto px-6 py-4">
          <div class="space-y-4">
            <div v-for="group in timezoneGroups" :key="group.region" class="border-b pb-4 last:border-b-0">
              <h4 class="font-medium text-gray-800 mb-2 sticky top-0 bg-white py-1">{{ group.region }}</h4>
              <div class="grid grid-cols-1 gap-1">
                <label v-for="tz in group.zones" :key="tz.value"
                  class="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input type="radio" :value="tz.value" v-model="selectedTimezone" class="mr-3 text-blue-600" />
                  <div class="flex-1">
                    <div class="text-sm font-medium">{{ tz.city }}</div>
                    <div class="text-xs text-gray-500">{{ tz.offset }}</div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Fixed bottom buttons -->
        <div class="flex justify-end space-x-3 p-6 pt-4 border-t border-gray-200 bg-white">
          <button @click="closeTimezoneModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
            Cancel
          </button>
          <button @click="saveTimezone"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors">
            Save
          </button>
        </div>
      </div>
    </div>
  </header>


</template>
<script setup>
const isMenuOpen = ref(false)

// Only fetch current user on client-side to avoid SSR/prerender issues
const { currentUser: loggedInUser, isLoading, isSignedIn, isLoaded } = process.client 
  ? useCurrentUser() 
  : { currentUser: ref(null), isLoading: ref(false), isSignedIn: ref(false), isLoaded: ref(false) }

// Timezone functionality
const { currentTimezone, setTimezone } = useTimezone()
const isTimezoneModalOpen = ref(false)
const selectedTimezone = ref(currentTimezone.value)

// Watch currentTimezone and update selectedTimezone when it changes
watch(currentTimezone, (newTimezone) => {
  if (!isTimezoneModalOpen.value) {
    selectedTimezone.value = newTimezone
  }
})

const handleMenuClick = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const openTimezoneModal = () => {
  console.log('Opening timezone modal, current timezone:', currentTimezone.value)
  selectedTimezone.value = currentTimezone.value
  isTimezoneModalOpen.value = true
}

const closeTimezoneModal = () => {
  console.log('Closing timezone modal')
  isTimezoneModalOpen.value = false
}

const saveTimezone = () => {
  console.log('Saving timezone:', selectedTimezone.value)
  console.log('Current timezone before save:', currentTimezone.value)
  setTimezone(selectedTimezone.value)
  console.log('Current timezone after save:', currentTimezone.value)
  closeTimezoneModal()
}

// Display timezone as friendly city name
const displayTimezone = computed(() => {
  const tz = currentTimezone.value
  if (!tz) return 'Local'

  // Extract city name from timezone (e.g., "America/New_York" -> "New York")
  const cityName = tz.split('/').pop()?.replace('_', ' ') || tz

  // Return formatted city name
  return cityName
})

// Timezone groups data
const timezoneGroups = [
  {
    region: 'North America',
    zones: [
      { value: 'America/New_York', city: 'New York (EST/EDT)', offset: 'UTC-5/-4' },
      { value: 'America/Chicago', city: 'Chicago (CST/CDT)', offset: 'UTC-6/-5' },
      { value: 'America/Denver', city: 'Denver (MST/MDT)', offset: 'UTC-7/-6' },
      { value: 'America/Los_Angeles', city: 'Los Angeles (PST/PDT)', offset: 'UTC-8/-7' },
      { value: 'America/Toronto', city: 'Toronto (EST/EDT)', offset: 'UTC-5/-4' },
      { value: 'America/Vancouver', city: 'Vancouver (PST/PDT)', offset: 'UTC-8/-7' }
    ]
  },
  {
    region: 'Europe',
    zones: [
      { value: 'Europe/London', city: 'London (GMT/BST)', offset: 'UTC+0/+1' },
      { value: 'Europe/Paris', city: 'Paris (CET/CEST)', offset: 'UTC+1/+2' },
      { value: 'Europe/Berlin', city: 'Berlin (CET/CEST)', offset: 'UTC+1/+2' },
      { value: 'Europe/Rome', city: 'Rome (CET/CEST)', offset: 'UTC+1/+2' },
      { value: 'Europe/Madrid', city: 'Madrid (CET/CEST)', offset: 'UTC+1/+2' },
      { value: 'Europe/Amsterdam', city: 'Amsterdam (CET/CEST)', offset: 'UTC+1/+2' }
    ]
  },
  {
    region: 'Asia Pacific',
    zones: [
      { value: 'Asia/Tokyo', city: 'Tokyo (JST)', offset: 'UTC+9' },
      { value: 'Asia/Shanghai', city: 'Shanghai (CST)', offset: 'UTC+8' },
      { value: 'Asia/Hong_Kong', city: 'Hong Kong (HKT)', offset: 'UTC+8' },
      { value: 'Asia/Singapore', city: 'Singapore (SGT)', offset: 'UTC+8' },
      { value: 'Asia/Kolkata', city: 'Mumbai (IST)', offset: 'UTC+5:30' },
      { value: 'Australia/Sydney', city: 'Sydney (AEST/AEDT)', offset: 'UTC+10/+11' }
    ]
  },
  {
    region: 'Other',
    zones: [
      { value: 'UTC', city: 'Coordinated Universal Time', offset: 'UTC+0' },
      { value: 'America/Sao_Paulo', city: 'São Paulo (BRT/BRST)', offset: 'UTC-3/-2' },
      { value: 'Africa/Cairo', city: 'Cairo (EET/EEST)', offset: 'UTC+2/+3' },
      { value: 'Pacific/Auckland', city: 'Auckland (NZST/NZDT)', offset: 'UTC+12/+13' }
    ]
  }
]

const isClient = computed(() => {
  return isLoaded.value && isSignedIn.value && loggedInUser.value?.roles?.includes('client')
})
const isEditor = computed(() => {
  return isLoaded.value && isSignedIn.value && loggedInUser.value?.roles?.includes('editor')
})
const isSuperAdmin = computed(() => {
  return isLoaded.value && isSignedIn.value && loggedInUser.value?.roles?.includes('super-admin')
})

// Display primary role for user badge
const userRole = computed(() => {
  if (!isLoaded.value || !loggedInUser.value) return ''
  if (isSuperAdmin.value) return 'Super Admin'
  if (isEditor.value) return 'Editor'
  if (isClient.value) return 'Client'
  return ''
})
</script>

<script>


export default {
  name: 'SiteHeader',
  data() {
    return {
      route: this.$route
    }
  }
}
</script>

<style scoped>
/* Add any specific styles for the header here */
</style>
